import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isClientRole, isPrestataireRole } from '../utils/roleUtils';

interface TabsConfig {
  entityType: 'operation' | 'commande';
  getCommandeType?: (entity: any) => 'MS' | 'BC' | 'BCI';
}

export const useSuiviEntityTabs = (entity: any, config: TabsConfig) => {
  const { user } = useAuth();
  const isClientView = isClientRole(user?.role || '');
  const isPrestataireView = isPrestataireRole(user?.role || '');

  // CORRECTION: Définir getCommandeType AVANT de l'utiliser
  const getCommandeType = (entityData: any): 'MS' | 'BC' | 'BCI' => {
    if (config.getCommandeType) {
      return config.getCommandeType(entityData);
    }
    return entityData?.type || entityData?.operation?.type || 'MS';
  };

  // Maintenant on peut utiliser getCommandeType
  const commandeType = getCommandeType(entity);

  // CORRECTION: Définir la fonction getTabs et calculer tabs AVANT de les utiliser
  const getTabs = useCallback((type: 'MS' | 'BC' | 'BCI', isClientView: boolean, isPrestataireView: boolean) => {
    const baseTabs = {
      'MS': {
        client: ['Historique', 'Consultation', 'Analyse', 'Notifications et signatures', 'Messagerie', 'Évaluation'],
        prestataire: ['Historique', 'Candidature', 'Notifications et signatures', 'Messagerie']
      },
      'BC': {
        // NOUVEAU: Logique BC avec cascade dynamique
        client: [], // Sera calculé dynamiquement
        prestataire: ['Historique', 'Consultation', 'Offre', 'Messagerie', "Évaluation"] // Fixe pour les prestataires
      },
      'BCI': {
        // NOUVEAU: Logique BCI avec cascade dynamique
        client: [], // Sera calculé dynamiquement
        prestataire: ['Offre'] // Fixe pour les prestataires
      }
    };

    if (type === 'BC' || type === 'BCI') {
      if (isClientView && entity) {
        // CORRECTION: Pour les acheteurs BC/BCI, afficher les onglets selon l'historique des rangs
        const tabs = [];
        if (type === 'BC') {
          tabs.push('Historique');
        }

        const rangActuel = entity.rang_actuel || 1;

        const status = entity.status?.nom;
        const statusNorm = (status || '').toLowerCase();
        const commandes = entity.commandes || [];

        console.log('=== GÉNÉRATION ONGLETS ACHETEUR ' + type + ' ===');
        console.log('Rang actuel:', rangActuel);
        console.log('Status:', status);
        console.log('Commandes disponibles:', commandes);

        // NOUVEAU: Analyser l'historique des commandes pour déterminer les onglets à afficher
        const rangsTraites = new Set<number>();
        const rangsAcceptes = new Set<number>();
        const rangsRefuses = new Set<number>();
        const rangsExpires = new Set<number>();
        const rangsAVoir = new Set<number>();

        // Parcourir toutes les commandes pour identifier l'historique
        commandes.forEach((cmd: any) => {
          const rang = parseInt(cmd.rang);
          rangsTraites.add(rang);

          if (cmd.id_status === 7) { // Accepté
            rangsAcceptes.add(rang);
          } else if (cmd.id_status === 8) { // Refusé
            rangsRefuses.add(rang);
          } else if (cmd.id_status === 9) { // Expiré
            rangsExpires.add(rang);
          }
        });

        console.log('Rangs traités:', Array.from(rangsTraites));
        console.log('Rangs acceptés:', Array.from(rangsAcceptes));
        console.log('Rangs refusés:', Array.from(rangsRefuses));
        console.log('Rangs expirés:', Array.from(rangsExpires));

        // CORRECTION: Logique de cascade - afficher les onglets selon les refus

        // NOUVEAU: Récupérer le statut global de l'opération (pas juste des commandes)
        const operationStatusId = entity.id_status;
        const operationStatusNom = (entity.status?.nom || '').toLowerCase();
        const isOperationTerminee = operationStatusId === 7 || operationStatusId === 8 || operationStatusId === 9;
        // 7 = Accepté, 8 = Refusé, 9 = Expiré

        console.log('Statut opération globale - ID:', operationStatusId, 'Nom:', operationStatusNom, 'Terminée:', isOperationTerminee);

        // NOUVEAU: Si on est en brouillon, afficher seulement le rang 1 (comparaison insensible à la casse)
        if (statusNorm.includes('brouillon')) {
          rangsAVoir.add(1);
        } else if (isOperationTerminee) {
          // CORRECTION: Si l'opération est terminée (Accepté/Refusé/Expiré), 
          // afficher uniquement les rangs JUSQU'AU rang accepté (pas les rangs après)
          // Trouver le rang accepté s'il y en a un
          let rangAccepte = 0;
          rangsAcceptes.forEach(rang => {
            if (rang > rangAccepte) rangAccepte = rang;
          });
          
          if (rangAccepte > 0) {
            // Afficher tous les rangs jusqu'au rang accepté inclus
            for (let r = 1; r <= rangAccepte; r++) {
              rangsAVoir.add(r);
            }
            console.log('Opération terminée avec rang accepté:', rangAccepte, '- affichage rangs 1 à', rangAccepte);
          } else {
            // Pas de rang accepté, afficher tous les rangs traités (refusés/expirés)
            rangsTraites.forEach(rang => rangsAVoir.add(rang));
            console.log('Opération terminée sans acceptation - affichage des rangs traités:', Array.from(rangsAVoir));
          }
        } else {
          // CORRECTION: Toujours afficher le rang 1
          rangsAVoir.add(1);

          // CORRECTION BCI: Pour BCI, ne jamais afficher de rangs suivants après un refus
          if (type === 'BCI') {
            // Pour BCI, on s'arrête toujours au rang 1
            console.log('BCI: Affichage uniquement du rang 1');
          } else {
            // CORRECTION: Pour BC, afficher le rang suivant UNIQUEMENT si le rang précédent est refusé ou expiré
            // ET que le rang suivant n'a pas été accepté au-dessus
            
            // Rang 2 : affiché seulement si rang 1 refusé/expiré OU si rang 2 a été traité (accepté ou refusé)
            if (rangsRefuses.has(1) || rangsExpires.has(1)) {
              rangsAVoir.add(2);
              
              // Rang 3 : affiché seulement si rang 2 AUSSI refusé ou expiré
              // CORRECTION: Ne PAS afficher rang 3 si rang 2 a été ACCEPTÉ
              if ((rangsRefuses.has(2) || rangsExpires.has(2)) && !rangsAcceptes.has(2)) {
                rangsAVoir.add(3);
              }
            }

            // CORRECTION: Si un rang supérieur est accepté, afficher tous les rangs jusqu'à celui-ci
            // mais NE PAS afficher les rangs après un rang accepté
            if (rangsAcceptes.has(2)) {
              rangsAVoir.add(2);
              // Ne pas ajouter rang 3 car rang 2 est accepté
            }
            if (rangsAcceptes.has(3)) {
              rangsAVoir.add(2);
              rangsAVoir.add(3);
            }
          }
        }

        // CORRECTION: Générer les onglets dans l'ordre des rangs
        const rangsOrdonnes = Array.from(rangsAVoir).sort((a, b) => a - b);

        rangsOrdonnes.forEach(rang => {
          tabs.push(`Consultation Rang ${rang}`);
          tabs.push(`Offre Rang ${rang}`);
        });
        if (type === 'BC') {
          tabs.push('Messagerie');
          tabs.push('Évaluation');
        }
        console.log('Onglets générés pour acheteur ' + type + ':', tabs);
        console.log('Rangs affichés:', rangsOrdonnes);

        return tabs;
      } else if (isPrestataireView) {
        // CORRECTION: Pour les prestataires BC/BCI, toujours inclure l'onglet Offre
        let baseTabs: string[] = []
        if (type === 'BC') {
          baseTabs = ['Historique', 'Consultation', 'Offre', 'Messagerie', "Évaluation"];
        } else if (type === 'BCI') {
          // BCI: prestataire voit uniquement l'onglet Offre
          baseTabs = ['Offre'];
        }

        console.log('=== GÉNÉRATION ONGLETS PRESTATAIRE ' + type + ' ===');
        console.log('Onglets générés:', baseTabs);
        console.log('Status entité:', entity?.status?.nom);

        return baseTabs;
      }
    }

    // Logique par défaut pour MS
    if (isClientView) {
      return baseTabs[type].client;
    } else if (isPrestataireView) {
      return baseTabs[type].prestataire;
    }

    return [];
  }, [entity]); // CORRECTION: Ajouter entity dans les dépendances

  // CORRECTION: Calculer tabs ici, AVANT de les utiliser dans getDisabledTabs
  const tabs = entity ? getTabs(commandeType, isClientView, isPrestataireView) : [];

  // Déterminer l'onglet actif initial
  const [activeTab, setActiveTab] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const tabFromUrl = params.get('tab');

    console.log('=== useState INITIAL ACTIVE TAB ===');
    console.log('Tab from URL:', tabFromUrl);
    console.log('Entity:', entity);
    console.log('CommandeType:', commandeType);
    console.log('IsClientView:', isClientView);
    console.log('IsPrestataireView:', isPrestataireView);

    if (tabFromUrl) {
      console.log('useState: Utilisation du tab URL:', parseInt(tabFromUrl, 10));
      return parseInt(tabFromUrl, 10);
    }

    // NOUVEAU: Pour les prestataires BC/BCI, par défaut sur le premier onglet
    if (entity && (commandeType === 'BC' || commandeType === 'BCI') && isPrestataireView) {
      console.log('useState: Prestataire ' + commandeType + ' - défaut index 0');
      return 0;
    }

    // NOUVEAU: Pour les acheteurs BCI, défaut sur Consultation Rang 1 (index 0)
    if (entity && commandeType === 'BCI' && isClientView) {
      console.log('useState: Acheteur BCI - défaut index 0 (Consultation Rang 1)');
      return 0;
    }

    if (entity && commandeType === 'BC' && isClientView) {
      const status = entity.status?.nom;
      const statusNorm = (status || '').toLowerCase();
      const isTransferred = entity.transferred === true;
      const commandes = entity.commandes || [];
      const operationStatusId = entity.id_status;

      // CORRECTION: Pour les BC en brouillon, aller directement sur "Consultation Rang 1" (index 1)
      if (statusNorm.includes('brouillon')) {
        return 1; // Onglet "Consultation Rang 1"
      }

      // NOUVEAU: Pour les opérations terminées (Validé/Accepté), aller sur l'onglet Offre du rang accepté
      if (operationStatusId === 7) { // Accepté / Validé
        // Trouver le rang qui a été accepté
        const rangAccepte = commandes.find((cmd: any) => cmd.id_status === 7);
        if (rangAccepte) {
          const rang = parseInt(rangAccepte.rang);
          // Calculer l'index de l'onglet Offre pour ce rang
          // Onglets: Historique(0), Consultation Rang 1(1), Offre Rang 1(2), [Consultation Rang 2(3), Offre Rang 2(4), ...]
          // Pour rang 1: Offre = index 2, Pour rang 2: Offre = index 4, Pour rang 3: Offre = index 6
          const offreIndex = rang * 2; // Historique(0) + (rang * 2 pour Consultation et Offre)
          console.log(`useState: Opération validée, rang ${rang} accepté → Offre index: ${offreIndex}`);
          return offreIndex;
        }
      }

      // CORRECTION: Pour panier normal, vérifier le status des commandes individuelles (pas le status de l'opération)
      if (!isTransferred && (status === 'À analyser' || entity.id_status === 12)) {
        // Si pas de commandes, initialiser à 0 et laisser useEffect gérer
        if (!commandes || commandes.length === 0) {
          console.log('useState: Pas de commandes, initialisation à 0');
          return 0;
        }

        const rangsOrdonnes = commandes
          .map((cmd: any) => ({
            rang: parseInt(cmd.rang),
            id_status: cmd.id_status,
            hasArticles: cmd.articles && cmd.articles.length > 0,
            refuse: cmd.id_status === 8,
            expire: cmd.id_status === 9,
            refuseOuExpire: cmd.id_status === 8 || cmd.id_status === 9,
            accepte: cmd.id_status === 7
          }))
          .sort((a: any, b: any) => a.rang - b.rang);

        console.log('=== useState INITIAL "À ANALYSER" ===');
        console.log('Rangs disponibles:', rangsOrdonnes);

        const rang1Info = rangsOrdonnes.find((r: any) => r.rang === 1);
        const rang2Info = rangsOrdonnes.find((r: any) => r.rang === 2);
        const rang3Info = rangsOrdonnes.find((r: any) => r.rang === 3);

        console.log('Rang 1 refusé/expiré?', rang1Info?.refuseOuExpire, '(id_status:', rang1Info?.id_status, ')');
        console.log('Rang 2 articles?', rang2Info?.hasArticles);

        // CORRECTION: Si rang 1 a refusé ou expiré (id_status === 8 ou 9), aller sur Offre Rang 2
        if (rang1Info && rang1Info.refuseOuExpire) {
          if (rang2Info && rang2Info.hasArticles) {
            console.log('useState: Rang 1 refusé/expiré → index: 4');
            return 4;
          }

          // Si rang 2 aussi a refusé ou expiré, aller sur Offre Rang 3
          if (rang2Info && rang2Info.refuseOuExpire && rang3Info && rang3Info.hasArticles) {
            console.log('useState: Rangs 1 et 2 refusés/expirés → index: 6');
            return 6;
          }
        }

        // Par défaut, aller sur Offre Rang 1
        console.log('useState: Défaut → index: 2');
        return 2;
      }

      // NOUVEAU: Pour panier inversé avec statut "À analyser" ou "En attente de décision de l'acheteur"
      if (isTransferred && (status === 'À analyser' || status === 'En attente de décision de l\'acheteur' || entity.id_status === 13)) {
        // Trier les rangs et chercher le premier qui n'a pas refusé ni expiré
        const rangsOrdonnes = commandes
          .map((cmd: any) => ({
            rang: parseInt(cmd.rang),
            id_status: cmd.id_status,
            hasArticles: cmd.articles && cmd.articles.length > 0,
            refuse: cmd.id_status === 8,
            expire: cmd.id_status === 9,
            refuseOuExpire: cmd.id_status === 8 || cmd.id_status === 9,
            enAttente: cmd.id_status === 13 || cmd.id_status === 1
          }))
          .sort((a: any, b: any) => a.rang - b.rang);

        console.log('useState: Panier inversé - Rangs disponibles:', rangsOrdonnes);

        // CORRECTION: Pour l'acheteur en panier inversé, aller sur la CONSULTATION (pas l'offre)
        // Chercher le premier rang qui n'a PAS refusé ni expiré
        for (const rangInfo of rangsOrdonnes) {
          if (!rangInfo.refuseOuExpire) {
            // Index de Consultation = (rang - 1) * 2 + 1
            // Rang 1: index 1, Rang 2: index 3, Rang 3: index 5
            const consultationIndex = (rangInfo.rang - 1) * 2 + 1;
            console.log(`useState: Panier inversé rang ${rangInfo.rang} actif → Consultation index: ${consultationIndex}`);
            return consultationIndex;
          }
        }

        // Fallback: si tous ont refusé ou expiré, aller sur Consultation Rang 1
        console.log('useState: Panier inversé fallback → Consultation Rang 1, index: 1');
        return 1;
      }
    }

    return 0;
  });

  // NOUVEAU: useEffect pour mettre à jour l'onglet actif après le chargement des données
  useEffect(() => {
    console.log('=== useEffect TRIGGERED ===');
    console.log('Entity:', entity);
    console.log('CommandeType:', commandeType);
    console.log('IsClientView:', isClientView);
    console.log('Current activeTab:', activeTab);

    // Si pas d'entity, ne rien faire
    if (!entity) {
      console.log('useEffect: Pas d\'entity, sortie');
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const tabFromUrl = params.get('tab');
    const tabFromUrlNum = tabFromUrl ? parseInt(tabFromUrl) : null;

    // CORRECTION: Si l'URL spécifie un onglet et qu'il est différent de l'onglet actuel, forcer le changement
    // Cela permet de respecter la navigation depuis la liste des commandes
    if (tabFromUrlNum !== null && tabFromUrlNum !== activeTab) {
      console.log('useEffect: Tab depuis URL différent de activeTab, changement forcé:', tabFromUrlNum);
      setActiveTab(tabFromUrlNum);
      return;
    }

    if (commandeType === 'BC' && isClientView) {
      const status = entity.status?.nom;
      const isTransferred = entity.transferred === true;
      const commandes = entity.commandes || [];

      console.log('=== useEffect: CONDITIONS BC ACHETEUR (SANS REDIRECTION) ===');
      console.log('Status:', status);
      console.log('IsTransferred:', isTransferred);
      console.log('Commandes:', commandes);

      // CORRECTION: Logique de vérification SANS redirection automatique
      // Pour panier normal, vérifier le status des commandes individuelles
      if (!isTransferred && (status === 'À analyser' || entity.id_status === 12)) {
        // Vérifier que les commandes sont bien chargées
        if (!commandes || commandes.length === 0) {
          console.log('useEffect: Commandes pas encore chargées');
          return;
        }

        const rangsOrdonnes = commandes
          .map((cmd: any) => ({
            rang: parseInt(cmd.rang),
            id_status: cmd.id_status,
            hasArticles: cmd.articles && cmd.articles.length > 0,
            refuse: cmd.id_status === 8, // Refusé
            expire: cmd.id_status === 9, // Expiré
            refuseOuExpire: cmd.id_status === 8 || cmd.id_status === 9, // Refusé ou Expiré
            accepte: cmd.id_status === 7, // Accepté
            attente: cmd.id_status === 1 || cmd.id_status === 13 // En attente / Brouillon
          }))
          .sort((a: any, b: any) => a.rang - b.rang);

        console.log('=== VÉRIFICATION PANIER NORMAL "À ANALYSER" ===');
        console.log('Rangs disponibles avec détails:', rangsOrdonnes);

        const rang1Info = rangsOrdonnes.find((r: any) => r.rang === 1);
        const rang2Info = rangsOrdonnes.find((r: any) => r.rang === 2);
        const rang3Info = rangsOrdonnes.find((r: any) => r.rang === 3);

        console.log('Rang 1 Info:', rang1Info);
        console.log('Rang 2 Info:', rang2Info);
        console.log('Rang 3 Info:', rang3Info);

        // Logique de détermination sans redirection
        if (rang1Info && rang1Info.refuseOuExpire) {
          console.log('✓ Rang 1 a refusé/expiré (id_status = 8 ou 9) - Rang 2 disponible');
          if (rang2Info && rang2Info.hasArticles) {
            console.log('✓ Rang 2 a des articles - Consultation Rang 2 disponible');
          } else if (rang2Info && rang2Info.refuseOuExpire && rang3Info && rang3Info.hasArticles) {
            console.log('✓ Rang 2 aussi refusé/expiré - Consultation Rang 3 disponible');
          }
        } else if (rang1Info && rang1Info.accepte) {
          console.log('✓ Rang 1 a accepté - Offre Rang 1 active');
        } else {
          console.log('✓ Rang 1 en attente - Offre Rang 1 active');
        }
      } else if (isTransferred && (status === 'À analyser' || status === 'En attente de décision de l\'acheteur' || entity.id_status === 13)) {
        // Pour panier inversé - Vérification sans redirection
        console.log('=== VÉRIFICATION PANIER INVERSÉ "À ANALYSER" / "EN ATTENTE DÉCISION" ===');

        const rangsOrdonnes = commandes
          .map((cmd: any) => ({
            rang: parseInt(cmd.rang),
            id_status: cmd.id_status,
            hasArticles: cmd.articles && cmd.articles.length > 0,
            refuse: cmd.id_status === 8, // Refusé
            expire: cmd.id_status === 9, // Expiré
            refuseOuExpire: cmd.id_status === 8 || cmd.id_status === 9, // Refusé ou Expiré
            accepte: cmd.id_status === 7, // Accepté
            attente: cmd.id_status === 1 || cmd.id_status === 13 // En attente / Brouillon
          }))
          .sort((a: any, b: any) => a.rang - b.rang);

        console.log('Rangs disponibles:', rangsOrdonnes);

        // Vérification du rang actif (non refusé)
        for (const rangInfo of rangsOrdonnes) {
          if (!rangInfo.refuse) {
            console.log(`✓ Rang ${rangInfo.rang} actif (pas refusé) - Consultation disponible`);
            break;
          }
        }
      } else {
        console.log('useEffect: Aucune condition spéciale - Navigation libre');
      }
    }
  }, [entity, commandeType, isClientView]);

  // NOUVEAU: Fonction pour déterminer les onglets désactivés selon la cascade BC/BCI
  const getDisabledTabs = useCallback(() => {
    if (!entity) return [];

    let disabledTabs: number[] = [];
    const status = entity.status?.nom || entity.status;
    const statusNorm = (status || '').toLowerCase();

    // NOUVEAU: Logique spécifique BCI
    if (commandeType === 'BCI') {
      if (isClientView) {
        // Pour BCI acheteur: uniquement les onglets du rang 1
        const commandes = entity.commandes || [];
        const rang1Info = commandes.find((cmd: any) => parseInt(cmd.rang) === 1);

        console.log('=== DEBUG ONGLETS DÉSACTIVÉS ACHETEUR BCI ===');
        console.log('Status:', status);
        console.log('Rang 1 info:', rang1Info);

        // "Consultation Rang 1" est à l'index 0
        // "Offre Rang 1" est à l'index 1

        // Si brouillon ou en consultation, désactiver l'Offre
        if (statusNorm.includes('brouillon') || statusNorm.includes('consultation')) {
          disabledTabs.push(1); // Désactiver "Offre Rang 1"
          console.log('BCI Brouillon/Consultation - Offre désactivée');
        }

        // Si le rang 1 n'a pas encore d'articles/offre, désactiver l'Offre
        if (!rang1Info || !rang1Info.articles || rang1Info.articles.length === 0) {
          if (!disabledTabs.includes(1)) {
            disabledTabs.push(1); // Désactiver "Offre Rang 1"
          }
          console.log('BCI Pas d\'articles - Offre désactivée');
        }

        console.log('Onglets désactivés BCI:', disabledTabs);
        return disabledTabs;
      } else if (isPrestataireView) {
        // Pour BCI prestataire: uniquement l'onglet Offre, pas d'onglets désactivés
        return [];
      }
    }

    if (commandeType === 'BC') {
      if (isClientView) {
        // CORRECTION: Pour les acheteurs BC - nouvelle logique basée sur l'historique
        const currentRang = entity.rang_actuel || 1;
        const statusId = entity.id_status;
        const commandes = entity.commandes || [];

        console.log('=== DEBUG ONGLETS DÉSACTIVÉS ACHETEUR BC ===');
        console.log('Current rang:', currentRang);
        console.log('Status:', status);
        console.log('Commandes:', commandes);

        // NOUVEAU: Analyser l'historique pour déterminer l'état de chaque rang
        const rangStates = new Map<number, { consulte: boolean, offreDisponible: boolean, accepte: boolean, refuse: boolean }>();

        // Initialiser les états par défaut
        for (let rang = 1; rang <= 3; rang++) {
          rangStates.set(rang, {
            consulte: false,
            offreDisponible: false,
            accepte: false,
            refuse: false
          });
        }

        // NOUVEAU: Analyser chaque commande pour déterminer l'état
        const isTransferred = entity.transferred === true;

        commandes.forEach((cmd: any) => {
          const rang = parseInt(cmd.rang);
          const cmdStatus = cmd.id_status;
          const hasArticles = cmd.articles && cmd.articles.length > 0;

          if (rangStates.has(rang)) {
            const state = rangStates.get(rang)!;
            state.consulte = true; // Si une commande existe, elle a été consultée

            // NOUVEAU: Pour panier normal (transferred: false), l'offre est disponible dès qu'il y a des articles
            if (!isTransferred && hasArticles) {
              state.offreDisponible = true;
            }

            if (cmdStatus === 7) { // Accepté
              state.accepte = true;
              state.offreDisponible = true;
            } else if (cmdStatus === 8) { // Refusé
              state.refuse = true;
              state.offreDisponible = true;
            } else if (cmdStatus === 9) { // CORRECTION: Expiré - traiter comme un refus pour la cascade
              state.refuse = true; // Permet le déblocage du rang suivant
              state.offreDisponible = true; // L'acheteur peut voir l'état de l'offre expirée
            } else if (cmdStatus === 13) { // En attente decision
              state.offreDisponible = true;
            }
          }
        });

        // NOUVEAU: Pour panier inversé, si un rang a refusé, débloquer la consultation ET l'offre du rang suivant
        if (isTransferred) {
          console.log('Panier inversé - Déblocage cascade après refus');
          for (let rang = 1; rang <= 2; rang++) {
            const state = rangStates.get(rang);
            if (state && state.refuse) {
              // Débloquer la consultation et l'offre du rang suivant
              const nextRang = rang + 1;
              const nextState = rangStates.get(nextRang);
              if (nextState) {
                nextState.consulte = true;
                // Ne pas forcer offreDisponible pour consultation, l'acheteur doit pouvoir relancer
                console.log(`Rang ${rang} refusé → Déblocage Consultation Rang ${nextRang}`);
              }
            }
          }

          // CORRECTION: Pour panier inversé, si une commande a le statut 15 (proposition faite), activer son offre
          commandes.forEach((cmd: any) => {
            const rang = parseInt(cmd.rang);
            if (cmd.id_status === 15) { // Proposition faite par le prestataire
              const state = rangStates.get(rang);
              if (state) {
                state.offreDisponible = true;
                console.log(`Rang ${rang} - offre disponible (proposition faite, id_status = 15)`);
              }
            }
          });
        }

        console.log('États des rangs calculés:', Object.fromEntries(rangStates));
        console.log('Is transferred:', isTransferred);

        // NOUVEAU: Vérifier aussi le statut global pour "En attente de votre acceptation"
        if (status === 'À accepter') {
          // Identifier le rang concerné par ce statut
          const rangConcerne = currentRang; // ou entity.rang si disponible
          const state = rangStates.get(rangConcerne);
          if (state) {
            state.offreDisponible = true; // L'offre est disponible pour ce rang
            console.log(`Rang ${rangConcerne} - offre disponible via statut "À accepter"`);
          }
        }

        // NOUVEAU: Pour panier normal avec statut "À analyser", toutes les offres avec articles sont disponibles
        // CORRECTION: Inclure aussi les commandes expirées (id_status = 9) ou refusées (id_status = 8) pour consultation
        if (!isTransferred && status === 'À analyser') {
          commandes.forEach((cmd: any) => {
            const rang = parseInt(cmd.rang);
            const hasArticles = cmd.articles && cmd.articles.length > 0;
            const isRefused = cmd.id_status === 8;
            const isExpired = cmd.id_status === 9;
            const state = rangStates.get(rang);
            // L'offre est disponible si elle a des articles OU si elle est refusée/expirée (pour consultation)
            if (state && (hasArticles || isRefused || isExpired)) {
              state.offreDisponible = true;
              console.log(`Rang ${rang} - offre disponible via statut "À analyser" (panier normal, hasArticles: ${hasArticles}, refusé: ${isRefused}, expiré: ${isExpired})`);
            }
          });
        }

        const statusNorm = (status || '').toLowerCase();

        if (statusNorm === 'refusé' || statusNorm === 'refuse') {
          // Identifier le rang concerné par ce statut
          const rangConcerne = currentRang; // ou entity.rang si disponible
          const state = rangStates.get(rangConcerne);
          if (state) {
            state.offreDisponible = true; // L'offre est disponible pour ce rang
            console.log(`Rang ${rangConcerne} - offre disponible via statut "Refusé"`);
          }
        }

        // CORRECTION: Brouillon - seuls Historique et Consultation Rang 1 accessibles (insensible à la casse)
        if (statusNorm.includes('brouillon')) {
          tabs.forEach((tab, index) => {
            if (tab !== 'Historique' &&
              tab !== 'Consultation Rang 1' &&
              tab !== 'Messagerie' &&
              tab !== 'Évaluation') {
              disabledTabs.push(index);
            }
          });
        } else {
          // NOUVEAU: Après brouillon, logique basée sur l'état réel et la cascade
          tabs.forEach((tab, index) => {
            // CORRECTION: Consultation Rang X - accessible si le rang est visible dans les onglets
            if (tab.startsWith('Consultation Rang ')) {
              const rang = parseInt(tab.match(/\d+/)?.[0] || '0');
              const state = rangStates.get(rang);

              // NOUVEAU: Le rang doit être consulté OU être le rang actuel dans la cascade
              const isRangAccessible = state?.consulte || (rang === currentRang);

              if (!isRangAccessible) {
                console.log(`Désactivation Consultation Rang ${rang} - pas accessible dans la cascade`);
                disabledTabs.push(index);
              }
            }

            // CORRECTION: Offre Rang X - accessible seulement si une offre est disponible
            if (tab.startsWith('Offre Rang ')) {
              const rang = parseInt(tab.match(/\d+/)?.[0] || '0');
              const state = rangStates.get(rang);

              console.log(`=== DEBUG OFFRE RANG ${rang} ===`);
              console.log('State:', state);
              console.log('Offre disponible:', state?.offreDisponible);
              console.log('Is transferred:', isTransferred);
              console.log('Current status:', status);

              // CORRECTION: Pour panier inversé CÔTÉ ACHETEUR
              // L'onglet Offre doit être accessible quand l'acheteur doit CONSULTER et VALIDER l'offre du prestataire
              // C'est le cas pour le statut "À accepter" (ID 4)
              let shouldDisable = false;

              if (isTransferred) {
                // Pour panier inversé côté acheteur:
                // - ACTIVER l'onglet Offre si le prestataire a fait une proposition (offre disponible)
                // - DÉSACTIVER l'onglet Offre sinon (l'acheteur ne remplit jamais l'offre en panier inversé)

                // CORRECTION: Vérifier si une offre est disponible pour ce rang (état de la commande)
                const cmdCeRang = commandes.find((cmd: any) => parseInt(cmd.rang) === rang);
                const offrePrestataireDispo = cmdCeRang && (
                  cmdCeRang.id_status === 15 || // Proposition faite
                  cmdCeRang.id_status === 7 ||  // Accepté
                  cmdCeRang.id_status === 8     // Refusé
                );

                if (state?.offreDisponible || offrePrestataireDispo) {
                  shouldDisable = false;
                  console.log(`Offre Rang ${rang} ACTIVÉE pour validation en panier inversé (proposition disponible)`);
                } else {
                  shouldDisable = true;
                  console.log(`Offre Rang ${rang} DÉSACTIVÉE en panier inversé côté acheteur (pas de proposition)`);
                }
              } else {
                // CORRECTION: Pour panier normal, vérifier si la consultation du rang est lancée
                // Si c'est un rang > 1, vérifier que la consultation a été lancée (id_status !== 1)
                const commandes = entity.commandes || [];
                const cmdCeRang = commandes.find((cmd: any) => parseInt(cmd.rang) === rang);
                const consultationNonLancee = cmdCeRang && cmdCeRang.id_status === 1; // Brouillon

                if (rang > 1 && consultationNonLancee) {
                  shouldDisable = true;
                  console.log(`Offre Rang ${rang} DÉSACTIVÉE - consultation pas encore lancée (id_status = 1)`);
                } else {
                  // Pour panier normal: désactiver si pas d'offre disponible
                  shouldDisable = !state?.offreDisponible;
                }
              }

              if (shouldDisable) {
                console.log(`Désactivation Offre Rang ${rang} - pas d'offre disponible`);
                disabledTabs.push(index);
              }
            }
          });
        }

        console.log('Onglets désactivés pour acheteur BC:', disabledTabs);
      } else if (isPrestataireView) {
        // CORRECTION: Pour les prestataires BC
        const rangPrestataire = entity.rang || 1;
        const status = entity.status?.nom;
        const statusId = entity.status?.id;

        console.log('=== DEBUG ONGLETS PRESTATAIRE BC ===');
        console.log('Status:', status);
        console.log('Status ID:', statusId);
        console.log('Rang prestataire:', rangPrestataire);
        console.log('Onglets disponibles:', tabs);

        // NOUVEAU: Liste complète des statuts où le prestataire peut accéder à l'onglet Offre
        const prestataireCanAccessOffre =
          // Statuts de consultation/attente
          // status === 'En consultation' ||
          status?.startsWith('En attente de l\'acceptation du rang') || // Pattern pour "En attente de l'acceptation du rang X"
          // NOUVEAU: Variations possibles du statut d'attente
          status === 'À accepter' ||
          status === 'À chiffrer' ||  // CORRECTION: Ajout du statut "À chiffrer" (ID 11)
          status === "En attente de décision de l'acheteur" ||  // CORRECTION: Statut après envoi de l'offre (ID 13)
          // Statuts finaux (pour consultation en lecture seule)
          status === 'Accepté' || status === 'Valider' || status === 'Valide' ||
          status === 'Validé' ||
          status === 'Refusé' || status === 'Refuser' ||
          status === 'Expiré' || status === 'Expirer';

        console.log('Statut actuel:', status);
        console.log('Prestataire peut accéder à Offre:', prestataireCanAccessOffre);

        if (prestataireCanAccessOffre) {
          // NOUVEAU: Prestataire peut accéder à tous ses onglets
          console.log('Prestataire - accès complet aux onglets');
          // Pas d'onglets désactivés
        } else {
          // NOUVEAU: Statuts où l'onglet Offre doit être désactivé
          console.log('Prestataire - onglet Offre désactivé pour statut:', status, 'ID:', statusId);
          const offreIndex = tabs.indexOf('Offre');
          if (offreIndex !== -1) {
            disabledTabs.push(offreIndex);
          }
        }
      }
    } else {
      // GARDER: Logique MS existante
      if (isClientView) {
        if (statusNorm.includes('brouillon')) {
          const analyseIndex = tabs.indexOf('Analyse');
          const notifIndex = tabs.indexOf('Notifications et signatures');

          if (analyseIndex !== -1) disabledTabs.push(analyseIndex);
          if (notifIndex !== -1) disabledTabs.push(notifIndex);
        } else {
          // Pour les acheteurs MS, désactiver tant que l'analyse n'est pas terminée
          const notifIndex = tabs.indexOf('Notifications et signatures');
          const entityStatusId = entity.id_status;
          if (notifIndex !== -1 && entityStatusId && entityStatusId < 8) {
            disabledTabs.push(notifIndex);
          }
        }
      } else if (isPrestataireView) {
        // GARDER: Logique MS prestataires
        const notifIndex = tabs.indexOf('Notifications et signatures');

        if (notifIndex !== -1) {
          if (status === 'En consultation' ||
            status === 'Candidature en cours' ||
            status === 'À candidater') {
            disabledTabs.push(notifIndex);
          }
        }
      }
    }

    console.log('Onglets désactivés:', disabledTabs);
    return disabledTabs;
  }, [entity, commandeType, tabs, config.entityType, isClientView, isPrestataireView]);

  const getInactiveTabs = useCallback(() => {
    if (!entity) return [];
    const inactiveTabs: number[] = [];

    const messagerieIndex = tabs.indexOf('Messagerie');
    const evaluationIndex = tabs.indexOf('Évaluation');
    if (messagerieIndex !== -1) inactiveTabs.push(messagerieIndex);
    if (evaluationIndex !== -1) inactiveTabs.push(evaluationIndex);

    return inactiveTabs;
  }, [tabs]);

  const handleTabChange = (tabIndex: number) => {
    const disabledTabs = getDisabledTabs();
    const inactiveTabs = getInactiveTabs();

    if (!disabledTabs.includes(tabIndex) && !inactiveTabs.includes(tabIndex)) {
      setActiveTab(tabIndex);

      // CORRECTION: Mettre à jour l'URL pour refléter le changement d'onglet
      // Cela évite que le useEffect force un retour à l'onglet précédent
      const params = new URLSearchParams(window.location.search);
      params.set('tab', tabIndex.toString());
      window.history.replaceState({}, '', `${window.location.pathname}?${params.toString()}`);
    }
  };

  return {
    activeTab,
    setActiveTab,
    tabs,
    commandeType,
    isClientView,
    isPrestataireView,
    getDisabledTabs,
    getInactiveTabs,
    handleTabChange
  };
};